/**
 * Marketing Tile CTAs (PGP)
 *
 * Nosto's search template renders the marketing tiles (.marketing-banner-item) from
 * window.marketingBanners (layout/theme.liquid), with the CTA inside .banner-content,
 * so it follows the eyebrow / heading / subheading text position.
 *
 * This script post-processes each tile Nosto inserts:
 *  - Moves the CTA out of .banner-content into its own .banner-ctas row, pinned to the
 *    bottom of the tile and aligned left / center / right (banner_cta_alignment).
 *  - Adds the optional second CTA (banner_cta_text_2 / banner_cta_url_2), cloned from
 *    the first so it shares its styles and tracking attributes.
 *  - Adds the optional image link (banner_image_url): the whole tile links there, for
 *    tiles with no text overlay or CTA. CTAs stay on top and keep their own URLs.
 *
 * Nosto re-renders the grid on filter / sort / pagination, so new tiles are picked up
 * with a MutationObserver. Each tile is processed once (.has-banner-ctas).
 */
(() => {
  const banners = (window.marketingBanners || []).map((banner) => banner.collection_entry || {});
  if (!banners.length) return;

  const TILE_SELECTOR = '.marketing-banner';
  const PROCESSED_CLASS = 'has-banner-ctas';
  const ALIGNMENTS = ['left', 'center', 'right'];
  // Flags set by the external click / impression tracker on the original CTA.
  // Stripped from the clone so the second CTA is tracked as its own link.
  const TRACKER_STATE_ATTRIBUTES = /^data-(.*-tracked|impression-id|above-fold)$/;

  // "//cdn.shopify.com/.../file.jpg?v=1&width=1200" -> "/.../file.jpg"
  const imagePath = (url) => {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  };

  const urlPath = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname + parsed.search;
    } catch (error) {
      return url;
    }
  };

  // Match a rendered tile to its metaobject entry by image, then CTA URL.
  // Falls back to the tile's order on the page (Nosto keeps the entries' order).
  const findBanner = (tile, tileIndex) => {
    const tileImages = [...tile.querySelectorAll('img, source')]
      .map((el) => imagePath(el.getAttribute('src') || (el.getAttribute('srcset') || '').split(/[\s,]/)[0]))
      .filter(Boolean);
    const ctaHref = urlPath(tile.querySelector('.banner-button')?.getAttribute('href'));

    const imageMatches = banners.filter((banner) =>
      [imagePath(banner.image), imagePath(banner.image_mobile)].some((path) => path && tileImages.includes(path)),
    );

    if (imageMatches.length === 1) return imageMatches[0];

    const candidates = imageMatches.length ? imageMatches : banners;
    const ctaMatches = candidates.filter((banner) => ctaHref && urlPath(banner.banner_cta_url) === ctaHref);
    if (ctaMatches.length === 1) return ctaMatches[0];

    return banners[tileIndex] || null;
  };

  const createSecondCta = (firstCta, banner) => {
    let secondCta;
    if (firstCta) {
      secondCta = firstCta.cloneNode(false);
      [...secondCta.attributes].forEach(({ name }) => {
        if (TRACKER_STATE_ATTRIBUTES.test(name)) secondCta.removeAttribute(name);
      });
    } else {
      secondCta = document.createElement('a');
      secondCta.className = 'banner-button';
    }
    secondCta.href = banner.banner_cta_url_2;
    secondCta.textContent = banner.banner_cta_text_2;
    return secondCta;
  };

  // Plain text of a (possibly rich text) field, parsed inertly
  const textOf = (html) =>
    html ? new DOMParser().parseFromString(html, 'text/html').body.textContent.trim() : '';

  const addImageLink = (tile, banner) => {
    const link = document.createElement('a');
    link.className = 'banner-image-link';
    link.href = banner.banner_image_url;
    link.dataset.category = 'pgp-banner';
    link.dataset.action = 'promo_bar_click';
    link.dataset.label = 'image';
    link.setAttribute(
      'aria-label',
      [banner.banner_heading, banner.banner_sub_heading, banner.banner_description, banner.banner_cta_text]
        .map(textOf)
        .find(Boolean) ||
        tile.querySelector('img')?.alt ||
        'Shop now',
    );
    tile.appendChild(link);
  };

  const addCtaRow = (tile, banner) => {
    const firstCta = tile.querySelector('.banner-content .banner-button');
    const hasSecondCta = Boolean(banner && banner.banner_cta_text_2 && banner.banner_cta_url_2);
    if (!firstCta && !hasSecondCta) return;

    const alignment = ALIGNMENTS.includes(banner?.banner_cta_alignment) ? banner.banner_cta_alignment : 'right';
    const ctaRow = document.createElement('div');
    ctaRow.className = `banner-ctas banner-ctas--${alignment}`;

    if (firstCta) ctaRow.appendChild(firstCta);
    if (hasSecondCta) ctaRow.appendChild(createSecondCta(firstCta, banner));

    tile.appendChild(ctaRow);

    // Keep bottom-positioned text clear of the CTA row, even when the CTAs wrap.
    const setRowHeight = () => tile.style.setProperty('--banner-ctas-height', `${ctaRow.offsetHeight}px`);
    setRowHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setRowHeight).observe(ctaRow);
  };

  const processTile = (tile, tileIndex) => {
    if (tile.classList.contains(PROCESSED_CLASS)) return;
    // Nosto may re-render the tile's class list; the tile is already processed.
    if (tile.querySelector(':scope > .banner-ctas, :scope > .banner-image-link')) {
      tile.classList.add(PROCESSED_CLASS);
      return;
    }

    const banner = findBanner(tile, tileIndex);
    if (banner?.banner_image_url) addImageLink(tile, banner);
    addCtaRow(tile, banner);
    tile.classList.add(PROCESSED_CLASS);
  };

  const processAll = () => {
    document.querySelectorAll(TILE_SELECTOR).forEach(processTile);
  };

  const observer = new MutationObserver((mutations) => {
    const hasNewTile = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches(TILE_SELECTOR) || node.querySelector(TILE_SELECTOR)),
      ),
    );
    if (hasNewTile) processAll();
  });

  processAll();
  observer.observe(document.body, { childList: true, subtree: true });
})();
